import { useCallback, useMemo } from 'react';
import { isArray } from '@beeline.com/bp-design-system';
import { useTypedBulkMutation } from 'utils/hooks/useTypedBulkMutation';
import { RsiData, RsiFieldKey, RsiTableOnErrorCallback, RsiTableValidator } from '../../../Types';
import {
  isFieldValueNull,
  ReferenceIdValidationModel,
  ValidationParams
} from '../../../validations/impl/validationUtils';
import { ValidateImporterColumnDocument } from '../../../validations/data/__generated__/ValidateImporterColumn.graphql';
import { FieldMetadataElementValidator } from 'src/components/fieldMetadata/FieldMetadataElementValidator';

interface ValidatorPayload {
  fieldsForExistenceDetection: RsiFieldKey[];
  fieldsForReferenceIdValidation: RsiFieldKey[];
  rowIsCreateMessage: string;
  rowIsUpdateMessage: string;
}

interface ColumnValidatorEntry {
  validator: ColumnValidator;
  payload: ColumnReferenceIdValidationModel;
}

interface ColumnValidator {
  keys: RsiFieldKey[];
  onErrors(result: ReferenceIdValidationModel[], addError: RsiTableOnErrorCallback): void;
}

/**
 * Validate a table's rows against specific reference ID fields and check for field existence,
 * with support for displaying errors or warnings based on validation results.
 *
 * @return {Function} A callback function that accepts a table of data and an error callback, performs validations, and returns results.
 */
export function useValidateTableAgainstReferenceIdAndFieldExistence(
  {
    fieldsForReferenceIdValidation,
    fieldsForExistenceDetection,
    rowIsUpdateMessage,
    rowIsCreateMessage
  }: ValidatorPayload,
  validationParams: ValidationParams | undefined,
  fieldMetadataValidator: FieldMetadataElementValidator
): RsiTableValidator {
  const getServerValidationErrors = useColumnValidationForUrnApi(validationParams);

  const validators: ColumnValidator[] = useMemo(
    () => [
      {
        keys: fieldsForExistenceDetection,
        onErrors(result, addError) {
          result.forEach((validation) => {
            if (validation && !validation.result.valid) {
              addError(
                validation.index,
                validation.urn,
                rowIsCreateMessage
                  ? {
                      message: rowIsCreateMessage,
                      level: 'warning'
                    }
                  : {
                      message: validation.result.message,
                      level: 'error'
                    }
              );
            } else if (validation && validation.result.valid) {
              addError(validation.index, validation.urn, { message: rowIsUpdateMessage, level: 'info' });
            }
          });
        }
      },
      {
        keys: fieldsForReferenceIdValidation,
        onErrors(result, addError) {
          result.forEach((validation) => {
            if (validation && !validation.result.valid) {
              addError(validation.index, validation.urn, { message: validation.result.message, level: 'error' });
            }
          });
        }
      }
    ],
    [fieldsForExistenceDetection, fieldsForReferenceIdValidation, rowIsUpdateMessage, rowIsCreateMessage]
  );

  return useCallback(
    async (table: RsiData[], addError: RsiTableOnErrorCallback) => {
      /**
       * Creates an array of objects containing the validator and its validation payload `ColumnValidatorEntry` and skips empty validation results
       * Then it performs server-side validation:
       *   - Maps the entries to extract their payloads
       *   - Calls with these payloads to validate against the server `getServerValidationErrors` JUST ONCE
       */

      const entries = validators.reduce<ColumnValidatorEntry[]>((prev, validator) => {
        const values = getColumnReferenceIdValidationModels(table, validator.keys, fieldMetadataValidator);
        if (values.length) {
          return [
            ...prev,
            ...values.map((payload) => ({
              validator,
              payload
            }))
          ];
        }
        return prev;
      }, []);

      const responses = await getServerValidationErrors(entries.map((entry) => entry.payload));

      responses.forEach((response, index) => {
        entries[index]?.validator.onErrors(response.validations ?? [], addError);
      });
    },
    [validators, getServerValidationErrors, fieldMetadataValidator]
  );
}

interface ColumnReferenceId {
  value: string;
  columnIndex: number;
}
interface ColumnReferenceIdValidationModel {
  urn: string;
  referenceIds: ColumnReferenceId[];
}

function getColumnReferenceIdValidationModels(
  table: RsiData[],
  rsiKeys: RsiFieldKey[],
  fieldMetadataValidator: FieldMetadataElementValidator
): ColumnReferenceIdValidationModel[] {
  const allRsiKeys = new Set(rsiKeys);
  const tableUrns = Array.from(new Set(table.map((row) => Object.keys(row)).flat())).filter((tableUrn) =>
    allRsiKeys.has(tableUrn)
  );
  return tableUrns.reduce<ColumnReferenceIdValidationModel[]>((prev, urn) => {
    const referenceIds = table.reduce<ColumnReferenceId[]>((acc, column, columnIndex) => {
      const value = column[urn];
      const isRequired = fieldMetadataValidator?.isFieldRequired(urn, {}, new Map());
      const isEmpty = !value || isFieldValueNull(value?.toString());

      // Skip optional empty values entirely
      if (!isRequired && isEmpty) {
        return acc;
      }

      if (isArray(value)) {
        return [
          ...acc,
          ...value.map((item) => ({
            value: item,
            columnIndex
          }))
        ];
      }

      return [
        ...acc,
        {
          value: value?.toString() ?? '',
          columnIndex
        }
      ];
    }, []);

    if (referenceIds.length) {
      return [
        ...prev,
        {
          urn,
          referenceIds
        }
      ];
    }
    return prev;
  }, []);
}

interface ServerValidationResponse {
  validations?: ReferenceIdValidationModel[] | null;
}

const useColumnValidationForUrnApi = (validationParams?: ValidationParams) => {
  const { set: validateImporterColumn } = useTypedBulkMutation(ValidateImporterColumnDocument);

  return useCallback(
    async (payload: ColumnReferenceIdValidationModel[]): Promise<ServerValidationResponse[]> => {
      if (!payload.length) {
        return [];
      }
      const resp = await validateImporterColumn(
        payload.map(({ urn, referenceIds }) => ({
          input: {
            urn,
            currentEntityReferenceId: validationParams?.targetReferenceId,
            values: referenceIds.map((referenceId) => ({
              value: referenceId.value,
              index: referenceId.columnIndex
            }))
          }
        }))
      );
      return resp as ServerValidationResponse[];
    },
    [validationParams, validateImporterColumn]
  );
};
